import React, { useState } from "react";
import styled from "styled-components";
import { useIntl, navigate } from "gatsby-plugin-intl";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { useStaticQuery, graphql } from "gatsby";
import get from "lodash/get";
import ReactPlayer from "react-player/vimeo";

import KCButton from "Components/KCButton";

const StyledWrapper = styled.div`
  color: ${(props) => props.theme.color[props.textColor]};
  background-color: ${(props) => props.theme.color[props.backgroundColor]};
  padding: 3rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledTitle = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-top: 1rem;
  color: ${(props) => props.theme.dimGrey};
  flex: 1;
  text-align: center;
`;

const StyledNavWrapper = styled.div`
  display: flex;
  margin: 0.6rem 0;
  text-align: center;
  gap: 0.4rem;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  align-items: center;
  ${(props) => props.theme.responsive.desktop} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const StyledCaseStudyTitle = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
`;

const StyledDescription = styled.div`
  margin: 0.2rem 0 0.8rem 0;
  font-size: 0.8rem;
  line-height: 1.1rem;
  opacity: 0.8;
  flex: 1;
  max-width: 100%;
  ${(props) => props.theme.responsive.desktop} {
    max-width: 26rem;
  }
`;

const StyledVideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 17rem;
  ${(props) => props.theme.responsive.desktop} {
    width: 26rem;
    height: 15rem;
  }
`;

const StyledReactPlayer = styled(ReactPlayer)`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 1;
  pointer-events: none;
`;

export default function CaseStudyListModal({
  title,
  caseStudies,
  textColor,
  backgroundColor,
}) {
  const [listIndex, setListIndex] = useState(0);
  const intl = useIntl();
  let caseStudy = caseStudies[listIndex];
  return (
    <StyledWrapper
      backgroundColor={get(backgroundColor, "color", null)}
      textColor={get(textColor, "color", null)}
    >
      <StyledTitle>{title[intl.locale]}</StyledTitle>
      {caseStudies.length > 1 && (
        <StyledNavWrapper>
          {caseStudies.map((study, i) => (
            <KCButton
              key={`grid-productNavButton-${i}`}
              handleClick={() => setListIndex(i)}
              size="small"
              buttonType={i === listIndex ? "primary" : "default"}
              label={get(study, `title[${intl.locale}]`, "")}
            />
          ))}
        </StyledNavWrapper>
      )}
      <StyledContent>
        <StyledVideoWrapper>
          {get(caseStudy, "vimeoURL", null) && (
            <StyledReactPlayer
              light={false}
              controls={false}
              url={get(caseStudy, "vimeoURL", null)}
              playing={true}
              loop={true}
              muted={true}
              playsinline={true}
              width="100%"
              height="100%"
              style={{
                left: "0",
              }}
            />
          )}

          {get(caseStudy, "image", null) && (
            <GatsbyImage
              style={{ width: "100%", height: "100%" }}
              imgStyle={{ objectFit: "cover" }}
              image={getImage(get(caseStudy, "image.imageFile", null))}
              alt="Image"
            />
          )}
        </StyledVideoWrapper>
        <div>
          <StyledCaseStudyTitle>
            {get(caseStudy, `title[${intl.locale}]`, null)}
          </StyledCaseStudyTitle>
          <StyledDescription>
            {get(caseStudy, `description[${intl.locale}]`, null)}
          </StyledDescription>
          <KCButton
            size="small"
            label="View Product"
            buttonType="primary"
            handleClick={() => {
              window.open(caseStudy.link, "_blank");
            }}
          />
        </div>
      </StyledContent>
    </StyledWrapper>
  );
}

export const query = graphql`
  fragment caseStudyListModal on STRAPI_ComponentBaseCaseStudyListModal {
    __typename
    id
    caseStudies {
      description {
        en
        es
        it
      }
      id
      link
      title {
        en
        es
        it
      }
      vimeoURL
      image {
        url
        imageFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
    title {
      en
      es
      id
      it
    }
    textColor {
      color
    }
    backgroundColor {
      color
    }
  }
`;
